/**
 * The top bar, shared by the Agent Hub and every app page — a Maritime AppBar.
 *
 * Left: the menu button (opens the apps sidebar), the SIYA logo and the page's title.
 * Right: the last-updated stamp, notifications, settings and the user's avatar.
 *
 * The return to SIYA Chat is the breadcrumb's home icon, not a link here; the rules for
 * WHETHER it is offered live in SimpleLayout.
 *
 * The gear opens /agent-hub/settings. The bell is still PRESENTATIONAL: no notifications
 * service exists, so it is aria-disabled and a screen reader does not promise a control that
 * does nothing. Give it an onClick the day there is somewhere to go.
 *
 * The title is the page's <h1>, in the leading slot: AppBar's own title is deliberately not a
 * heading, and the shell's pages have no other h1.
 */

import { Bell, Menu, Settings } from 'lucide-react';
import { AppBar } from '@syia-ai/maritimeai-ui';
import { Avatar, CountBadge, IconButton, IconLink } from '@/components/ui';

interface ShellHeaderProps {
  title: string;
  onOpenMenu: () => void;
  /** Hidden on external nodes, which suppress SIYA branding entirely. */
  showLogo: boolean;
  lastUpdated?: string;
  notificationCount?: number;
  /** Initials for the avatar. Empty until /api/me resolves. */
  initials: string;
  /** Full name, for the avatar's label. */
  userName?: string;
}

export default function ShellHeader({
  title,
  onOpenMenu,
  showLogo,
  lastUpdated,
  notificationCount = 0,
  initials,
  userName,
}: ShellHeaderProps) {
  return (
    <AppBar
      className="shell-header"
      // Brand and actions are wrapped in the shell's own containers so their spacing is the
      // design's, not AppBar's default gaps.
      leading={
        <div className="shell-header__brand">
          <IconButton label="Open menu" icon={<Menu />} onClick={onOpenMenu} />
          {showLogo && <img src="/siya.png" alt="" width={26} height={26} className="shell-header__logo" />}
          <h1 className="shell-header__title">{title}</h1>
        </div>
      }
      actions={
        <div className="shell-header__actions">
          {lastUpdated && (
            <div className="shell-header__stamp">
              <span className="shell-header__stamp-label">Last updated on</span>
              <span className="shell-header__stamp-value">{lastUpdated}</span>
            </div>
          )}

          {/* aria-disabled, not `disabled`: still readable and focusable, it just does not
              claim to do anything yet. */}
          <IconButton
            label={notificationCount > 0 ? `Notifications (${notificationCount})` : 'Notifications'}
            icon={<Bell />}
            aria-disabled="true"
          >
            <CountBadge count={notificationCount} />
          </IconButton>

          <IconLink to="/agent-hub/settings" label="Settings" icon={<Settings />} />

          <Avatar initials={initials} name={userName} />
        </div>
      }
    />
  );
}
